import { useEffect, useMemo, useState } from "react";
import {
  Card,
  Tag,
  Row,
  Col,
  Progress,
  Typography,
  Collapse,
  Button,
  Dropdown,
  Modal,
  Spin,
  message,
  MenuProps,
} from "antd";
import PendingApplicationsBanner from "./PendingApplicationsBanner";
import { CampaignListItem } from "interfaces/campaign-list-item";
import { AddLocationsToActiveCampaignResponse } from "interfaces/campaign-add-locations";
import { CampaignEdit } from "interfaces/campaign-edit";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarCheck,
  faClock,
  faClockFour,
  faEllipsisVertical,
  faEye,
  faFileSignature,
  faLocationDot,
  faPaperPlane,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import MiniTable from "components/mini-table";
import { ReactComponent as PlayIcon } from "assets/images/play-icon.svg";
import { ReactComponent as HeartIcon } from "assets/images/heart-icon.svg";
import { ReactComponent as ReachIcon } from "assets/images/reach-icon.svg";
import { ReactComponent as FlashIcon } from "assets/images/flash-icon.svg";
import { ReactComponent as MessageIcon } from "assets/images/message-text-icon.svg";
import { ReactComponent as RepeatIcon } from "assets/images/repeat-icon.svg";
import { ReactComponent as InstagramIcon } from "assets/images/instagram-icon.svg";
import { ReactComponent as TikTokIcon } from "assets/images/tiktok-icon.svg";
import { ReactComponent as PurchaseIcon } from "assets/images/profile-icon.svg";
import {
  formatNumberKMBT,
  ImgWithErrorHandling,
} from "pages/influencers/InfluencerCard";
import { openNewTabSafe } from "helpers/url-helpers";
import BoostProcurement from "./BoostProcurement";
import Loader from "components/loader/Loader";
import CampaignLocationsSelector, {
  CampaignLocationOption,
} from "components/campaign-locations-selector/CampaignLocationsSelector";
import {
  addLocationsToActiveCampaign,
  getCollabCampaignById,
  getCollabsCampaignList,
  deleteCollabsCampaign,
} from "services/SauceService";
import "./Campaigns.scss";
import { useSelector } from "react-redux";
import { BusinessState } from "redux-context/business";
import { CollabsState } from "redux-context/collabs";
import {
  getCampaignVenuesFromDetails,
} from "helpers/campaignCompensationPayload";
import { UserState } from "redux-context/user";
import LaunchCampaignPrompt from "components/launch-campaign-prompt";

interface ClicableLabelProps {
  className?: string;
  icon?: React.ReactNode;
  label?: string;
  value?: string;
  isClickable?: boolean;
  onClick?: () => void;
  extraInfo?: React.ReactNode;
}

const ClickableLabel: React.FC<ClicableLabelProps> = ({
  icon,
  label,
  value,
  isClickable,
  extraInfo,
  onClick,
}) => {
  return (
    <div
      className={`row ${isClickable ? "clickable" : ""}`}
      onClick={() => isClickable && onClick && onClick()}
    >
      {icon} <span style={{ fontWeight: 600 }}>{value}</span>
      <span className="decoration">{label}</span>
      {extraInfo}
    </div>
  );
};

enum Status {
  Active = "active",
  Pending = "pending",
  Completed = "completed",
  Cancelled = "cancelled",
}

// enum NewStatus {
//   Launched = "launched",
//   Pending = "pending",
//   Completed = "completed",
//   Cancelled = "cancelled",
// }

const statusLabels: Record<Status, string> = {
  [Status.Active]: "Active",
  [Status.Pending]: "Pending Launch",
  [Status.Completed]: "Completed",
  [Status.Cancelled]: "Cancelled",
};

const colorByStatus: Record<Status, string> = {
  [Status.Active]: "#52c41a",
  [Status.Pending]: "#faad14",
  [Status.Completed]: "#1890ff",
  [Status.Cancelled]: "#f5222d",
};

const mapVenueToSelectorOption = (venue: any): CampaignLocationOption => {
  return {
    uuid: venue?.uuid,
    name: venue?.name,
    address: venue?.address,
    addressDisplay: venue?.addressDisplay,
    cityState: venue?.cityState,
    city: venue?.city,
    state: venue?.state,
    country: venue?.country,
    disabled: venue?.disabled,
  };
};

const dedupeVenueUuids = (uuids: string[]): string[] => {
  return Array.from(new Set(uuids.filter(Boolean)));
};

const parseCampaignDetailsResponse = (response: unknown): CampaignEdit => {
  const candidate = response as CampaignEdit & { data?: CampaignEdit };
  return candidate?.data ?? candidate;
};

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const apiMessage = (
    error as {
      response?: {
        data?: {
          message?: string;
        };
      };
    }
  )?.response?.data?.message;

  return apiMessage || fallback;
};

const buildAddLocationsSummary = (
  response: AddLocationsToActiveCampaignResponse,
): string => {
  return [
    `Created: ${response.createdVenueUuids.length}`,
    `Already linked: ${response.alreadyLinkedVenueUuids.length}`,
    `Invalid: ${response.invalidVenueUuids.length}`,
    `Disabled: ${response.disabledVenueUuids.length}`,
  ].join(" · ");
};

const Campaigns: React.FC = () => {
  const [campaigns, setCampaigns] = useState<CampaignListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [addLocationsModalOpen, setAddLocationsModalOpen] =
    useState<boolean>(false);
  const [addLocationsModalLoading, setAddLocationsModalLoading] =
    useState<boolean>(false);
  const [addLocationsModalSaving, setAddLocationsModalSaving] =
    useState<boolean>(false);
  const [selectedCampaign, setSelectedCampaign] =
    useState<CampaignListItem | null>(null);
  const [selectedCampaignDetails, setSelectedCampaignDetails] =
    useState<CampaignEdit | null>(null);
  const [lockedVenueUuids, setLockedVenueUuids] = useState<string[]>([]);
  const [addedVenueUuids, setAddedVenueUuids] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { business, subscription } = useSelector(
    (state: BusinessState) => state.business,
  );
  const { numbers, loading: collabsLoading } = useSelector(
    (state: CollabsState) => state.collabs,
  );
  const { loggedUser } = useSelector((state: UserState) => state.user);
  const analyticsContext = useMemo(
    () => ({
      pathname: location.pathname,
      search: location.search,
      businessId: business?._id ?? null,
      userId: loggedUser?.id ?? null,
      userEmail: loggedUser?.email ?? null,
      planType: subscription?.planName ?? null,
      currency:
        subscription?.stripeDetails?.nextPaymentDetails?.currency ?? null,
    }),
    [
      business?._id,
      location.pathname,
      location.search,
      loggedUser?.email,
      loggedUser?.id,
      subscription?.planName,
      subscription?.stripeDetails?.nextPaymentDetails?.currency,
    ],
  );

  useEffect(() => {
    let mounted = true;
    const businessId = business?._id;

    if (!businessId) {
      return () => {
        mounted = false;
      };
    }

    const fetchCampaigns = async () => {
      if (mounted) setLoading(true);

      try {
        const response: any = await getCollabsCampaignList({
          businessId,
        });

        if (mounted) setCampaigns(response);
      } catch (err) {
        console.error("Failed to load campaigns", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchCampaigns();

    return () => {
      mounted = false;
    };
  }, [business?._id]);

  const hasResolvedCollabsCount =
    !collabsLoading && typeof numbers?.totalCollabs === "number";
  const showEmptyState =
    campaigns.length === 0 &&
    hasResolvedCollabsCount &&
    numbers.totalCollabs === 0;

  const onClickOpenPost = (url: string) => {
    if (!url) return;
    openNewTabSafe(url);
  };

  const handleCloseAddLocationsModal = () => {
    setAddLocationsModalOpen(false);
    setAddLocationsModalLoading(false);
    setAddLocationsModalSaving(false);
    setSelectedCampaign(null);
    setSelectedCampaignDetails(null);
    setLockedVenueUuids([]);
    setAddedVenueUuids([]);
  };

  const handleOpenAddLocationsModal = async (campaign: CampaignListItem) => {
    setSelectedCampaign(campaign);
    setAddLocationsModalOpen(true);
    setAddLocationsModalLoading(true);
    setAddLocationsModalSaving(false);
    setAddedVenueUuids([]);

    try {
      const campaignDetailsResponse = await getCollabCampaignById(campaign._id);
      const campaignDetails = parseCampaignDetailsResponse(
        campaignDetailsResponse as unknown,
      );
      const existingVenues = getCampaignVenuesFromDetails(campaignDetails);

      setSelectedCampaignDetails(campaignDetails);
      setLockedVenueUuids(existingVenues.map((venue) => venue.uuid));
    } catch (error) {
      console.error("Failed to load campaign details", error);
      message.error("Failed to load campaign details");
      handleCloseAddLocationsModal();
    } finally {
      setAddLocationsModalLoading(false);
    }
  };

  const addLocationsVenueOptions = useMemo(() => {
    const fromBusiness =
      business?.venues?.map((venue) => mapVenueToSelectorOption(venue)) || [];

    const existingCampaignVenues = selectedCampaignDetails
      ? getCampaignVenuesFromDetails(selectedCampaignDetails).map((venue) =>
          mapVenueToSelectorOption(venue),
        )
      : [];

    const mergedByUuid: Record<string, CampaignLocationOption> = {};

    [...fromBusiness, ...existingCampaignVenues].forEach((venue) => {
      if (!venue.uuid) return;
      mergedByUuid[venue.uuid] = {
        ...mergedByUuid[venue.uuid],
        ...venue,
      };
    });

    return Object.values(mergedByUuid);
  }, [business?.venues, selectedCampaignDetails]);

  const selectedVenueUuids = useMemo(() => {
    return dedupeVenueUuids([...lockedVenueUuids, ...addedVenueUuids]);
  }, [addedVenueUuids, lockedVenueUuids]);

  const handleAddVenueSelection = (uuid: string) => {
    if (!uuid || lockedVenueUuids.includes(uuid)) return;

    setAddedVenueUuids((previous) => {
      if (previous.includes(uuid)) return previous;
      return [...previous, uuid];
    });
  };

  const handleRemoveAddedVenue = (uuid: string) => {
    setAddedVenueUuids((previous) => previous.filter((item) => item !== uuid));
  };

  const handleSaveAddedLocations = async () => {
    if (!selectedCampaign) return;
    if (addedVenueUuids.length === 0) return;

    setAddLocationsModalSaving(true);

    try {
      const venueUuids = dedupeVenueUuids(addedVenueUuids);
      const response = await addLocationsToActiveCampaign({
        campaignId: selectedCampaign._id,
        venueUuids,
      });

      setCampaigns((previous) =>
        previous.map((campaign) => {
          if (campaign._id !== selectedCampaign._id) {
            return campaign;
          }

          return {
            ...campaign,
            collabs: dedupeVenueUuids([
              ...(campaign.collabs || []),
              ...response.createdVenueUuids,
            ]),
          };
        }),
      );

      message.success(buildAddLocationsSummary(response));
      handleCloseAddLocationsModal();
    } catch (error) {
      console.error("Failed to update campaign locations", error);
      message.error(
        getApiErrorMessage(error, "Failed to update campaign locations"),
      );
    } finally {
      setAddLocationsModalSaving(false);
    }
  };

  const handleRemoveCampaign = async (campaign: CampaignListItem) => {
    Modal.confirm({
      title: "Remove campaign",
      content: (
        <span>
          Are you sure you want to remove campaign <b>{campaign.name}</b>? This
          action cannot be undone.
        </span>
      ),
      okText: "Remove",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteCollabsCampaign(campaign._id);
          setCampaigns((prev) => prev.filter((c) => c._id !== campaign._id));
          message.success("Campaign removed");
        } catch (err) {
          console.error("Failed to remove campaign", err);
          message.error("Failed to remove campaign");
        }
      },
    });
  };

  const getMenuItems = (campaign: CampaignListItem): MenuProps["items"] => {
    const items: MenuProps["items"] = [
      {
        key: "1",
        label: "Edit Offer",
        onClick: () => {
          navigate(`/campaigns/${campaign._id}/edit-offer`);
        },
      },
      {
        type: "divider",
      },
      {
        key: "2",
        label: "Edit Instructions",
        onClick: () => {
          navigate(`/campaigns/${campaign._id}/edit-instructions`);
        },
      },
    ];

    if (campaign.status === Status.Active) {
      items.push(
        {
          type: "divider",
        },
        {
          key: "add-locations",
          label: "Add Locations",
          onClick: () => {
            handleOpenAddLocationsModal(campaign);
          },
        },
      );
    }

    if (campaign?.invitesSentAmount === 0) {
      items.push(
        {
          type: "divider",
        },
        {
          key: "remove",
          label: "Remove Campaign",
          onClick: () => handleRemoveCampaign(campaign),
        },
      );
    }

    return items;
  };

  if (showEmptyState && !loading) {
    return (
      <div className="campaigns">
        <LaunchCampaignPrompt analyticsContext={analyticsContext} />
      </div>
    );
  }

  return (
    <div
      className="campaigns"
      style={{ display: "flex", justifyContent: "center" }}
    >
      <Loader visible={loading} />
      <div style={{ maxWidth: 1400, width: "100%" }}>
        <PendingApplicationsBanner />
        <Modal
          title={
            selectedCampaign
              ? `Add locations to ${selectedCampaign.name}`
              : "Add locations"
          }
          open={addLocationsModalOpen}
          onCancel={handleCloseAddLocationsModal}
          onOk={handleSaveAddedLocations}
          okText="Save Locations"
          okButtonProps={{
            disabled: addedVenueUuids.length === 0 || addLocationsModalLoading,
          }}
          confirmLoading={addLocationsModalSaving}
          width={760}
        >
          {addLocationsModalLoading ? (
            <div style={{ padding: "24px 0", textAlign: "center" }}>
              <Spin />
            </div>
          ) : (
            <CampaignLocationsSelector
              venues={addLocationsVenueOptions}
              selectedVenueUuids={selectedVenueUuids}
              lockedVenueUuids={lockedVenueUuids}
              onSelectVenue={handleAddVenueSelection}
              onRemoveVenue={handleRemoveAddedVenue}
              onClearSelection={() => setAddedVenueUuids([])}
              placeholder="Search and add more locations..."
            />
          )}
        </Modal>
        {campaigns.map((campaign) => (
          <Card key={campaign._id || campaign.name}>
            <Row justify="space-between" align="middle">
              <Col>
                <div className="campaigns__header">
                  <span>{campaign.name}</span>
                  <Tag
                    color={
                      colorByStatus[campaign.status as Status] ?? "#d9d9d9"
                    }
                    className="campaigns__tag"
                  >
                    {statusLabels[campaign.status as Status]}
                  </Tag>
                </div>
                <div className="campaigns__dates">
                  {dayjs(campaign.startDate).format("MMMM D, YYYY")} -{" "}
                  {dayjs(campaign.endEnd).format("MMMM D, YYYY")}
                </div>
                <div className="campaigns__dates">
                  <FontAwesomeIcon icon={faLocationDot} /> Running across{" "}
                  {campaign?.collabs?.length} location
                </div>
              </Col>

              <Col
                style={{
                  textAlign: "right",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {campaign.status !== Status.Pending &&
                  campaign.status !== Status.Completed && (
                    <div>
                      <div className="campaigns__delivery-label">
                        Predicted delivery
                      </div>
                      <div className="campaigns__delivery-value">
                        {campaign.predictedDelivery
                          ? dayjs(campaign.predictedDelivery).format(
                              "MMMM D, YYYY",
                            )
                          : "N/A"}
                      </div>
                    </div>
                  )}
                {campaign.status === Status.Pending && (
                  <div>
                    <div className="campaigns__delivery-label">
                      Expected Launch
                    </div>
                    <div className="campaigns__delivery-value">
                      {campaign.expectedLaunch
                        ? dayjs(campaign.expectedLaunch).format("MMMM D, YYYY")
                        : "N/A"}
                    </div>
                  </div>
                )}
                <Dropdown menu={{ items: getMenuItems(campaign) }}>
                  <Button>
                    <FontAwesomeIcon icon={faEllipsisVertical} />
                  </Button>
                </Dropdown>
              </Col>
            </Row>
            <div className="campaigns__progress">
              <Progress
                percent={campaign.progress}
                showInfo={false}
                strokeColor={
                  campaign.status === Status.Pending ? "#faad14" : "#52c41a"
                }
                trailColor="#F2F2F2"
                strokeWidth={12}
              />
              <Row className="campaigns__progress-labels">
                <Col>Launched</Col>
                {/* <Col>Procuring</Col>
              <Col>Preparing</Col> */}
                <Col>Completed</Col>
              </Row>
            </div>
            <Row gutter={32} className="campaigns__section">
              <Col flex={1}>
                <div
                  className={`campaigns__reachout ${
                    campaign.status === Status.Pending
                      ? "campaigns__disabled"
                      : ""
                  }`}
                >
                  <div className="title">Influencer Reach Out</div>
                  <div className="row">
                    <FontAwesomeIcon icon={faPaperPlane} />
                    <span style={{ fontWeight: 600 }}>
                      {campaign.invitesSentAmount.toLocaleString()}
                    </span>{" "}
                    invites sent
                  </div>

                  <div className="calendar row" hidden={!campaign.lastReachOutAt}>
                    <FontAwesomeIcon icon={faClock} />{" "}
                    {dayjs(campaign.lastReachOutAt).fromNow()} last reach out
                  </div>
                  <div className="row">
                    <span style={{ height: 20 }}></span>
                  </div>
                </div>
              </Col>
              <Col flex={1}>
                <div
                  className={`campaigns__procuring ${
                    campaign.status === Status.Pending
                      ? "campaigns__disabled"
                      : ""
                  }`}
                  style={{
                    display: "flex",
                    gap: 16,
                    width: "100%",
                    justifyContent: "space-around",
                  }}
                >
                  <div>
                    <div className="title">
                      Procuring Creators{" "}
                      {campaign.lowConversion ? (
                        <Tag color="#faad14" className="campaigns__tag">
                          Low Conversion
                        </Tag>
                      ) : null}
                    </div>
                    {/* <div className="row">
                    <FontAwesomeIcon icon={faEye} />
                    <span style={{ fontWeight: 600 }}>
                      {campaign.viewedAmount.toLocaleString()}
                    </span>{" "}
                    Viewed
                  </div>
                  <div
                    className="row"
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate("/influencers?status=pending")}
                  >
                    <FontAwesomeIcon icon={faFileSignature} />
                    <span style={{ fontWeight: 600 }}>
                      {campaign.appliedAmount.toLocaleString()}
                    </span>{" "}
                    Applied
                  </div>

                  <div
                    className="row"
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate("/influencers?status=accepted")}
                  >
                    <FontAwesomeIcon icon={faCalendarCheck} />
                    <span style={{ fontWeight: 600 }}>
                      {campaign.bookedAmount.toLocaleString()}
                    </span>{" "}
                      
                  </div>
                    <div
                    className="row"
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate("/influencers?status=expired")}
                  >
                    <FontAwesomeIcon icon={faCalendarCheck} />
                    <span style={{ fontWeight: 600 }}>
                      {campaign.expiredAmount || "-"}
                    </span>{" "}
                    Expired
                  </div> */}
                    <ClickableLabel
                      icon={<FontAwesomeIcon icon={faEye} />}
                      label="Viewed"
                      value={campaign.viewedAmount.toLocaleString()}
                    />
                    <ClickableLabel
                      icon={<FontAwesomeIcon icon={faFileSignature} />}
                      label="Applied"
                      value={campaign.appliedAmount.toLocaleString()}
                      onClick={() =>
                        navigate(
                          "/applications?status=pending&campaignId=" +
                            campaign._id,
                        )
                      }
                      isClickable={campaign.appliedAmount > 0}
                      extraInfo={
                        campaign.lowConversion ? (
                          <div
                            style={{
                              background: "#FFEDE2",
                              color: "#eb580d",
                              padding: "2px 6px",
                              borderRadius: 4,
                              marginLeft: 8,
                            }}
                          >
                            {campaign.lowConversionRate || "-"}%
                          </div>
                        ) : null
                      }
                    />
                    <ClickableLabel
                      icon={<FontAwesomeIcon icon={faCalendarCheck} />}
                      label="Booked"
                      value={campaign.bookedAmount.toLocaleString()}
                      onClick={() => navigate("/applications?status=accepted")}
                      isClickable={campaign.bookedAmount > 0}
                    />

                    <ClickableLabel
                      icon={<FontAwesomeIcon icon={faClockFour} />}
                      label="Expired"
                      value={campaign.expiredAmount.toLocaleString()}
                      onClick={() => navigate("/applications?status=expired")}
                      isClickable={campaign.expiredAmount > 0}
                    />
                  </div>
                </div>
              </Col>
              <Col flex={1}>
                <div
                  className={`campaigns__content ${
                    campaign.status === Status.Pending
                      ? "campaigns__disabled"
                      : ""
                  }`}
                >
                  <MiniTable
                    rows={campaign.bookings}
                    emptyMessage="No Bookings Yet"
                  />
                </div>
              </Col>
              {/* <Col flex={1}>
              <div
                className={`campaigns__booking ${
                  campaign.status === Status.Pending
                    ? "campaigns__disabled"
                    : ""
                }`}
              >
                <div className="title">Accepted Booking</div>
                <div className="mini-table">
                  <div className="mini-table__header">
                    <div className="mini-table__col mini-table__col--photo"></div>
                    <div className="mini-table__col">Visit</div>
                    <div className="mini-table__col">Content due</div>
                  </div>
                  {[
                    {
                      photo: "https://randomuser.me/api/portraits/men/32.jpg",
                      visitDate: "2024-06-10",
                      contentDue: "2024-06-15",
                    },
                    {
                      photo: "https://randomuser.me/api/portraits/women/44.jpg",
                      visitDate: "2024-06-12",
                      contentDue: "2024-06-18",
                    },
                    {
                      photo: "https://randomuser.me/api/portraits/men/65.jpg",
                      visitDate: "2024-06-14",
                      contentDue: "2024-06-20",
                    },
                  ].map((row, idx) => (
                    <div className="mini-table__row" key={idx}>
                      <div className="mini-table__col mini-table__col--photo">
                        <img
                          src={row.photo}
                          alt="Influencer"
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            objectFit: "cover",
                            border: "1px solid #eee",
                          }}
                        />
                      </div>
                      <div className="mini-table__col">
                        {dayjs(row.visitDate).format("MMM D, YYYY")}
                      </div>
                      <div className="mini-table__col">
                        {dayjs(row.contentDue).format("MMM D, YYYY")}
                      </div>
                    </div>
                  ))}
                  <div
                    className="mini-table__footer"
                    style={{ textAlign: "center" }}
                  >
                    <span style={{ color: "#888" }}>
                      {" "}
                      <FontAwesomeIcon icon={faChevronDown} /> View more (21)
                    </span>
                  </div>
                </div>
              </div>
            </Col> */}
              {/* <Col flex={1}>
              <div
                className={`campaigns__content ${
                  campaign.status === Status.Pending
                    ? "campaigns__disabled"
                    : ""
                }`}
              >
                <div className="title">Content Submitted</div>
                <div className="row">
                  <CheckCircleFilled />
                  {campaign.contentSubmitted}/{campaign.contentSubmittedPending}
                </div>
                <div className="posts-live">Posts Live</div>
              </div>
            </Col> */}
            </Row>

            {campaign.lowConversion && <BoostProcurement campaign={campaign} />}
            <Collapse
              className="collapse"
              style={{ margin: "32px 0 0 0" }}
              defaultActiveKey={
                (campaign?.bookingsPosts?.length ?? 0) > 0
                  ? ["performance"]
                  : []
              }
            >
              <Collapse.Panel
                header={
                  <span style={{ fontWeight: 600, fontSize: 16 }}>
                    Overall Content Performance
                  </span>
                }
                key="performance"
              >
                <Row
                  gutter={[12, 12]}
                  className={
                    campaign.status === Status.Pending
                      ? "campaigns__disabled"
                      : ""
                  }
                >
                  <Col xs={4}>
                    <Card className="card-performance">
                      <ReachIcon className="icon" />
                      <Typography.Title level={5} style={{ margin: 0 }}>
                        {formatNumberKMBT(campaign?.bookingsStats?.totalReach)}
                      </Typography.Title>
                      <span>Reach</span>
                    </Card>
                  </Col>
                  <Col xs={4}>
                    <Card className="card-performance">
                      <PlayIcon className="icon" />
                      <Typography.Title level={5} style={{ margin: 0 }}>
                        {formatNumberKMBT(campaign?.bookingsStats?.totalViews)}
                      </Typography.Title>
                      <span>Plays</span>
                    </Card>
                  </Col>
                  <Col xs={4}>
                    <Card className="card-performance">
                      <HeartIcon className="icon" />
                      <Typography.Title level={5} style={{ margin: 0 }}>
                        {formatNumberKMBT(campaign?.bookingsStats?.totalLikes)}
                      </Typography.Title>
                      <span>Likes</span>
                    </Card>
                  </Col>
                  <Col xs={4}>
                    <Card className="card-performance">
                      <MessageIcon className="icon" />
                      <Typography.Title level={5} style={{ margin: 0 }}>
                        {formatNumberKMBT(
                          campaign?.bookingsStats?.totalComments,
                        )}
                      </Typography.Title>
                      <span>Comments</span>
                    </Card>
                  </Col>
                  <Col xs={4}>
                    <Card className="card-performance">
                      <RepeatIcon className="icon" />
                      <Typography.Title level={5} style={{ margin: 0 }}>
                        {formatNumberKMBT(
                          campaign?.bookingsStats?.totalReshares,
                        )}
                      </Typography.Title>
                      <span>Shares</span>
                    </Card>
                  </Col>
                  <Col xs={4}>
                    <Card className="card-performance">
                      <FlashIcon className="icon" />
                      <Typography.Title level={5} style={{ margin: 0 }}>
                        {campaign?.bookingsStats?.totalEngagement}%
                      </Typography.Title>
                      <span>Engagement</span>
                    </Card>
                  </Col>
                </Row>
              </Collapse.Panel>
            </Collapse>
            <Collapse
              defaultActiveKey={
                (campaign?.bookingsPosts?.length ?? 0) > 0 ? ["posts"] : []
              }
              className="collapse"
            >
              <Collapse.Panel
                header={
                  <span style={{ fontWeight: 600, fontSize: 16 }}>Posts</span>
                }
                key="posts"
              >
                <Row gutter={[12, 12]}>
                  <Col xs={24} className="list-post">
                    {campaign.bookingsPosts &&
                    campaign.bookingsPosts.length > 0 ? (
                      campaign.bookingsPosts.map((post: any, index) => (
                        <div
                          className="post"
                          key={`POST_${index}_${post.engagementRate}`}
                          onClick={() => onClickOpenPost(post.socialMediaUrl)}
                        >
                          <ImgWithErrorHandling src={post.coverImg} alt="" />
                          <span className="social-icon">
                            {post.socialMediaName?.toLowerCase() ===
                            "tiktok" ? (
                              <TikTokIcon className="stroke" />
                            ) : (
                              <InstagramIcon className="fill" />
                            )}
                          </span>
                          <div className="info">
                            <span className="text">
                              <FlashIcon /> {post.engagementRate || "-"}% Eng
                            </span>
                            <span className="text">
                              <PlayIcon /> {formatNumberKMBT(post.reach) || "-"}{" "}
                              views
                            </span>
                            {!!post.dinner && (
                              <span className="text">
                                <PurchaseIcon />
                                {post.dinner || "-"} Purchase Intent
                              </span>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-posts">
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                        <Typography.Title
                          level={5}
                          style={{ color: "#888", margin: 0 }}
                        >
                          No content available
                        </Typography.Title>
                        <Typography.Text style={{ color: "#888", margin: 0 }}>
                          Complete procurement to see posts
                        </Typography.Text>
                      </div>
                    )}
                  </Col>
                </Row>
              </Collapse.Panel>
            </Collapse>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Campaigns;
